/**
 * v2 Agentic-API client.
 *
 * All endpoints POST through the existing services/API.ts `create`
 * with `API_TYPE.AGENTIC`, so they inherit the Bearer-token forwarding
 * (MSAL or SKIP_AUTH bypass) and the HTTPError semantics.
 */
import { API_TYPE, create, load, streamRequest } from 'src/services/API';
import { HTTPError } from 'src/support/errors';

import {
  CardResolveBody,
  ConversationHistoryRow,
  HandlerResponse,
  PlanStatus,
  ResolveAction,
  StreamEvent,
} from './types';

// ---------------------------------------------------------------------------
// Session lifecycle — DB-minted session ids.
// ---------------------------------------------------------------------------
export interface SessionCreateResponse {
  session_id: string;
}

export async function createSession(clientId: number): Promise<string> {
  const resp = await create<SessionCreateResponse>(
    '/v2/sessions',
    { client_id: clientId },
    API_TYPE.AGENTIC
  );
  return resp.session_id;
}

// Drains an NDJSON stream from /v2/messages or /v2/cards/resolve. Calls
// onEvent for each parsed event and resolves with the final HandlerResponse.
// Throws HTTPError if the stream carries an error event so the existing
// formatError(e) path in callers keeps working unchanged.
async function consumeStream(
  response: Response,
  onEvent: (e: StreamEvent) => void
): Promise<HandlerResponse> {
  if (!response.body) {
    throw new Error('Streaming response has no body');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResponse: HandlerResponse | null = null;

  const handleLine = (line: string): void => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const event = JSON.parse(trimmed) as StreamEvent;
    onEvent(event);
    if (event.kind === 'final') {
      finalResponse = event.response;
    } else if (event.kind === 'error') {
      throw new HTTPError(response.url, event.status_code, event.message, {
        detail: event.message,
      });
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newlineIdx;
    while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, newlineIdx);
      buffer = buffer.slice(newlineIdx + 1);
      handleLine(line);
    }
  }
  // Flush any trailing line without a terminating newline.
  if (buffer.length > 0) {
    handleLine(buffer);
  }

  if (!finalResponse) {
    throw new Error('Stream ended without a final event');
  }
  return finalResponse;
}

export async function postMessage(
  text: string,
  sessionId: string,
  onEvent: (e: StreamEvent) => void
): Promise<HandlerResponse> {
  const resp = await streamRequest(
    '/v2/messages',
    { text, session_id: sessionId },
    API_TYPE.AGENTIC
  );
  return consumeStream(resp, onEvent);
}

export async function resolveCard(
  planId: string,
  expectedStatus: PlanStatus,
  action: ResolveAction,
  sessionId: string,
  onEvent: (e: StreamEvent) => void
): Promise<HandlerResponse> {
  const body: CardResolveBody = {
    plan_id: planId,
    expected_status: expectedStatus,
    action: action.action,
    session_id: sessionId,
  };
  if (action.action === 'modify') {
    body.text = action.text;
  }
  if (action.action === 'edit_field') {
    body.field_updates = action.field_updates;
  }
  const resp = await streamRequest('/v2/cards/resolve', body, API_TYPE.AGENTIC);
  return consumeStream(resp, onEvent);
}

// Re-export so callers don't need to dig into types directly for the event union.
export type { ConversationHistoryRow, StreamEvent };

// ---------------------------------------------------------------------------
// Scope envelope (per-user dialogue scope state)
// ---------------------------------------------------------------------------
export interface ScopeEntry {
  model_instance_id: number;
  scenario_id: number;
  tag?: string | null;
  forecast_model_name?: string | null;
  model_instance_name?: string | null;
  scenario_name?: string | null;
}

export interface ScopeEnvelope {
  entries: ScopeEntry[];
}

export interface ScopeSetResponse {
  row_id: string;
  scope_envelope: ScopeEnvelope;
}

export async function setScope(
  envelope: ScopeEnvelope,
  sessionId: string
): Promise<ScopeSetResponse> {
  return create<ScopeSetResponse>(
    '/v2/scope/set',
    { scope_envelope: envelope, session_id: sessionId },
    API_TYPE.AGENTIC
  );
}

// ---------------------------------------------------------------------------
// Scope panel options — the unified tree of v2-AI-enabled instances + the
// user's current scope envelope, scoped to the requested client.
// ---------------------------------------------------------------------------
export interface ScopeOptionsScenario {
  scenario_id: number;
  scenario_name: string;
  tag: string | null;
}

export interface ScopeOptionsInstance {
  forecast_model_id: number;
  forecast_model_name: string;
  model_instance_id: number;
  model_instance_name: string;
  scenarios: ScopeOptionsScenario[];
}

export interface ScopeOptionsResponse {
  instances: ScopeOptionsInstance[];
  current_scope: ScopeEnvelope | null;
}

export async function listScopeOptions(
  clientId: number,
  sessionId: string
): Promise<ScopeOptionsResponse> {
  return load(
    `/v2/clients/${clientId}/scope/options?session_id=${encodeURIComponent(sessionId)}`,
    undefined,
    API_TYPE.DEFAULT
  ) as Promise<ScopeOptionsResponse>;
}

/**
 * Current scope read endpoint on Agentic-API. Used post-mutation
 * (e.g., after the change_scope orchestration tool fires from chat)
 * to refresh the panel's In Scope tree without re-loading the full
 * options tree.
 */
export interface ScopeCurrentResponse {
  current_scope: ScopeEnvelope | null;
}

export async function getCurrentScope(
  sessionId: string
): Promise<ScopeCurrentResponse> {
  return load(
    `/v2/scope?session_id=${encodeURIComponent(sessionId)}`,
    undefined,
    API_TYPE.AGENTIC
  ) as Promise<ScopeCurrentResponse>;
}
